import Link from 'next/link';
import { useEffect, useState } from 'react';

import { NEXT_AUTH_METHOD } from '@app/constants/auth';
import { EMAIL_REGEX, PHONE_REGEX } from '@app/libs/services/Validation';
import { cn } from '@app/libs/utils';
import { authService } from '@app/services/auth.service';

import GCaptcha from '../GCaptcha';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';

interface RegisterComponentProps {
  className?: string;
  onRegister?: (...args: any[]) => void;
}

export const RegisterComponent: React.FC<RegisterComponentProps> = ({ className, onRegister }) => {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [captchaPassed, setCaptchaPassed] = useState(false);
  const [registerDisable, setRegisterDisable] = useState(true);

  const validateForm = () => {
    if (
      email.trim() === '' ||
      !(NEXT_AUTH_METHOD === 'phone' ? PHONE_REGEX.test(email) : EMAIL_REGEX.test(email)) ||
      !captchaPassed
    ) {
      setRegisterDisable(true);
    } else {
      setRegisterDisable(false);
    }
  };

  useEffect(() => {
    validateForm();
  }, [captchaPassed, email]);

  const handleRegister = async () => {
    try {
      const { success, errors } = await authService.otp(email);
      if (success) {
        onRegister?.(email);
      } else {
        toast.error(errors?.message || `Không gửi được OTP đến email ${email}`);
      }
    } catch {
      toast.error(`Không gửi được OTP đến email ${email}`);
    }
  };

  return (
    <div className={cn('max-w-[90vw] w-160 p-5 flex flex-col gap-20', className)}>
      <div className="flex flex-col gap-4 items-center">
        <h2 className="text-3xl font-bold">Đăng ký</h2>
        <p>
          Đã có tài khoản?{' '}
          <Link href="/auth/login" className="underline hover:text-blue-800 transition-all">
            Đăng nhập ngay
          </Link>
        </p>
      </div>

      <form className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <div className="flex justify-between gap-2">
            <label htmlFor="email">
              {NEXT_AUTH_METHOD === 'phone' ? 'Số điện thoại' : 'Email'} của bạn
            </label>
          </div>
          <input
            type={NEXT_AUTH_METHOD === 'phone' ? 'tel' : 'email'}
            id={NEXT_AUTH_METHOD === 'phone' ? 'phone' : 'email'}
            name={NEXT_AUTH_METHOD === 'phone' ? 'phone' : 'email'}
            className="w-full border border-gray-300 rounded-xl py-2 px-5 h-14"
            autoComplete={NEXT_AUTH_METHOD === 'phone' ? 'tel' : 'email'}
            placeholder={`Nhập ${NEXT_AUTH_METHOD === 'phone' ? 'số điện thoại' : 'email'} của bạn`}
            onChange={e => {
              if (NEXT_AUTH_METHOD === 'phone') {
                setEmail(e.target.value.replace(/[^0-9]/g, ''));
              } else {
                setEmail(e.target.value);
              }
            }}
          />
        </div>

        <div className="flex flex-col justify-center mt-5 gap-10">
          <GCaptcha
            className="w-full flex justify-center"
            onVerify={() => {
              setCaptchaPassed(true);
            }}
            onExpired={() => {
              setCaptchaPassed(false);
            }}
            onError={() => {
              setCaptchaPassed(false);
            }}
            onNoCaptcha={() => {
              setCaptchaPassed(true);
            }}
          />

          <p className="text-center text-sm">
            Bằng cách đăng ký, bạn đã đồng ý với
            <br />{' '}
            <Link href="/term" className="underline hover:text-blue-800 transition-all">
              Điều khoản dịch vụ
            </Link>{' '}
            và{' '}
            <Link href="/policy" className="underline hover:text-blue-800 transition-all">
              Chính sách bảo mật
            </Link>{' '}
            của chúng tôi
          </p>

          <Button
            type="button"
            className={cn(
              'w-full bg-blue-500 text-white rounded-xl py-2 px-5 h-14',
              registerDisable ? 'bg-gray-400 cursor-not-allowed' : 'cursor-pointer',
              'transition-all'
            )}
            disabled={registerDisable}
            onClick={handleRegister}
            loadingText="Đang đăng ký..."
          >
            Tiếp tục
          </Button>
        </div>
      </form>
    </div>
  );
};
